import React, { useState } from "react";
import { MessageCircle, X, Copy, Check, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { CONTACT } from "@/lib/api";
import { useT } from "@/lib/i18n";

const PHONE = CONTACT.whatsapp; // "4915208542673"
const DEFAULT_TEXT = "Hi Zopran, I'd like to discuss a logistics requirement.";

export function whatsappUrl(text = DEFAULT_TEXT) {
  return `https://api.whatsapp.com/send?phone=${PHONE}&text=${encodeURIComponent(text)}`;
}
export function whatsappAppUrl(text = DEFAULT_TEXT) {
  return `whatsapp://send?phone=${PHONE}&text=${encodeURIComponent(text)}`;
}

export function openWhatsApp(text) {
  const url = whatsappUrl(text);
  try {
    const w = window.open(url, "_blank", "noopener,noreferrer");
    if (!w) window.location.assign(url);
  } catch {
    window.location.assign(url);
  }
}

export default function WhatsAppFloat() {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const { t } = useT();
  const url = whatsappUrl();
  const appUrl = whatsappAppUrl();

  const copy = async (e) => {
    e?.preventDefault();
    try {
      await navigator.clipboard.writeText("+" + PHONE);
      setCopied(true);
      toast.success("WhatsApp number copied: +" + PHONE);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Number: +" + PHONE);
    }
  };

  return (
    <div className="fixed bottom-6 right-6 z-40 flex flex-col items-end gap-3">
      {open && (
        <div data-testid="whatsapp-popover" className="zp-card p-4 w-72 shadow-xl">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div>
              <div className="zp-label">{t("common.whatsapp")}</div>
              <div className="font-display tracking-tight mt-1 text-[15px]">+{PHONE}</div>
            </div>
            <button
              data-testid="whatsapp-close"
              onClick={() => setOpen(false)}
              className="w-8 h-8 grid place-items-center rounded-full border border-[color:var(--zp-border)] text-[color:var(--zp-text-2)] hover:bg-[color:var(--zp-bg-2)]"
              aria-label="Close"
            >
              <X size={14} />
            </button>
          </div>
          <div className="flex flex-col gap-2">
            <a
              data-testid="whatsapp-open-web"
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="zp-btn-cyan text-sm"
            >
              <MessageCircle size={14} /> Open WhatsApp <ExternalLink size={12} />
            </a>
            <a
              data-testid="whatsapp-open-app"
              href={appUrl}
              className="zp-btn-ghost text-sm"
            >
              Open WhatsApp app
            </a>
            <button data-testid="whatsapp-copy" onClick={copy} className="zp-btn-ghost text-sm">
              {copied ? <Check size={14} /> : <Copy size={14} />}
              {copied ? "Copied" : "Copy number"}
            </button>
          </div>
        </div>
      )}

      {/* Primary anchor — works as a native link AND toggles the popover on auxiliary click */}
      <a
        data-testid="whatsapp-float"
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={t("common.whatsapp")}
        onContextMenu={(e) => { e.preventDefault(); setOpen((v) => !v); }}
        onClick={(e) => {
          // hold Shift / right-click to reveal options; default click opens WhatsApp.
          if (e.shiftKey) {
            e.preventDefault();
            setOpen((v) => !v);
          }
        }}
        className="group relative"
      >
        <span className="relative inline-flex">
          <span className="absolute inset-0 rounded-full zp-ping" />
          <span className="relative w-14 h-14 grid place-items-center rounded-full bg-[#25D366] text-white shadow-[0_12px_36px_rgba(37,211,102,0.45)] border-2 border-white transition-transform group-hover:scale-105">
            <MessageCircle size={24} />
          </span>
        </span>
      </a>

      {/* Small overflow button so users can see all options without keyboard */}
      <button
        data-testid="whatsapp-more"
        onClick={() => setOpen((v) => !v)}
        aria-label="WhatsApp options"
        className="text-[10px] uppercase tracking-[0.18em] font-bold text-[color:var(--zp-text-2)] bg-white border border-[color:var(--zp-border)] rounded-full px-3 py-1 shadow-sm hover:bg-[color:var(--zp-bg-2)]"
      >
        Options
      </button>
    </div>
  );
}
